import { CircleAlert } from "lucide-react";
import { useToast } from "../components/ui/use-toast";

interface CustomToastBaseProps {
	title: string;
	description: string | React.ReactNode;
	duration?: number;
}

type CustomToastAllOptionalProps =
	| {
			title?: string;
			description?: string;
			duration?: number;
	  }
	| undefined;

export function useWarningToast() {
	const { toast } = useToast();

	const warningToast = ({
		title,
		description,
		duration,
	}: CustomToastBaseProps) => {
		toast({
			variant: "default",
			duration: duration ?? 15000,
			className:
				"text-sm bg-white bg-clip-padding bg-opacity-100 border-thin border-slate-200 md:bg-opacity-60",
			action: (
				<div className="w-full flex-col flex relative !ml-0">
					<div className="top-[-20px] left-[-40px] rounded-full w-64 h-64 absolute blur-xl bg-yellow-300"></div>

					<div className="flex items-center justify-center gap-5 relative z-[2]">
						<CircleAlert className="min-w-6" />
						<div>
							<div className="font-semibold">{title}</div>
							<div>{description}</div>
						</div>
					</div>
				</div>
			),
		});
	};

	return { warningToast };
}

export function useSuccessGradientToast() {
	const { toast } = useToast();

	const successToast = ({
		title,
		description,
		duration,
	}: CustomToastBaseProps) => {
		toast({
			variant: "default",
			duration: duration ?? 15000,
			className:
				"text-sm bg-slate-800 text-white bg-clip-padding bg-opacity-90 border-none",
			action: (
				<div className="w-full flex-col flex relative !ml-0">
					<div className="top-[-20px] left-[-40px] rounded-full w-64 h-64 absolute blur-xl bg-companyRed/50"></div>

					<div className="relative z-[2]">
						<div className="font-semibold">{title}</div>

						<div>{description}</div>

						{/* <div className="text-[0.75rem] mt-3 flex items-center gap-1">Swipe to dismiss <MoveRight className="mt-1" size={16}/></div> */}
					</div>
				</div>
			),
		});
	};

	return { successToast };
}

export function useIncompleteToast() {
	const { toast } = useToast();

	const incompleteToast = (props?: CustomToastAllOptionalProps) => {
		toast({
			variant: "destructive",
			title: props?.title ?? "Incomplete Section",
			description:
				props?.description ??
				"The form has redirected to an incomplete section. Please review and complete all required fields, then resubmit.",
			duration: props?.duration ?? 9000,
		});
	};

	return { incompleteToast };
}

export function useErrorToast() {
	const { toast } = useToast();

	const errorToast = (props?: CustomToastAllOptionalProps) => {
		toast({
			variant: "destructive",
			title: props?.title ?? "Something went wrong.",
			description: props?.description
				? `${props.description} Please refresh the page or contact our support team for assistance if the issue persists.`
				: "Please refresh the page or contact our support team for assistance if the issue persists.",
			duration: props?.duration ?? 7000,
		});
	};

	return { errorToast };
}
